import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CommentsField } from "@/shared/components/CommentsField";
import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import { DelayCodeTreePicker } from "@/shared/selectors/DelayCodeTreePicker";
import {
	dxtToLocalServerTime,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useDelayById } from "../../DelaysManagerPage";
import { DelayByShift } from "../../models/DelayByShift";
import { splitDelay } from "../../repositories/DelaysManagerRepository";
import CustomTimeSlider from "../others/CustomTimeSlider";

const formatDuration = (duration: number): string => {
	const hours = Math.floor(duration / (1000 * 60 * 60));
	const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((duration % (1000 * 60)) / 1000);
	return `${hours} hs ${minutes} min ${seconds}s`;
};

type Props = {
	show: boolean;
	onHide: (update: boolean) => void;
	delayId: number | null;
	assetId?: number;
	suffixTitle?: string;
};

interface CombinedDelayForm {
	delay1: DelayByShift;
	delay2: DelayByShift;
}

export const SplitDelaysModal = ({
	show,
	onHide,
	delayId,
	assetId,
	suffixTitle = "Delays Manager",
}: Props) => {
	const { state } = useUTLSettingsContext();
	const minDelayDuration = Number(get(state, `settings.MinValidDelay`, 10));

	const [delay, setDelay] = useState<DelayByShift | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [notificationErrorMessage, setNotificationErrorMessage] = useState("");

	const {
		data: delays,
		isLoading,
		isSuccess,
	} = useDelayById({
		delayId: delayId,
		onError: (e) => setError(e.message),
	});

	const onSubmit: SubmitHandler<CombinedDelayForm> = async (
		data: CombinedDelayForm,
	) => {
		if (
			data.delay1.End?.getTime()! - data.delay1.Start?.getTime()! <=
				minDelayDuration * 1000 ||
			data.delay2.End?.getTime()! - data.delay2.Start?.getTime()! <=
				minDelayDuration * 1000
		) {
			setNotificationErrorMessage(
				`The minimum duration allowed is ${minDelayDuration} seconds`,
			);
		} else {
			const resp = await splitDelay(data.delay1, data.delay2);
			if (resp.ok) {
				setNotificationMessage("The operation was completed successfully");
				onHide(true);
			} else {
				setError(resp.message);
			}
			onHide(false);
		}
	};

	const { control, handleSubmit, setValue, getValues, reset, watch } =
		useForm<CombinedDelayForm>({});

	const delay1End = watch("delay1.End");
	const delay1Start = watch("delay1.Start");
	const delay2End = watch("delay2.End");
	const delay2Start = watch("delay2.Start");

	useEffect(() => {
		if (show && delay) {
			reset();

			const startDate = delay?.Start ? new Date(delay?.Start) : new Date();
			const endDate = delay?.End ? new Date(delay?.End) : new Date();
			const middleDate = new Date(
				(startDate.getTime() + endDate.getTime()) / 2,
			);

			setValue("delay1.DelayId", delay.DelayId);
			setValue("delay1.Start", startDate);
			setValue("delay1.End", middleDate);
			setValue("delay2.Start", middleDate);
			setValue("delay2.End", endDate);
			setValue("delay1.DelayCategoryId", delay.DelayCategoryId);
			setValue("delay1.Comments", delay.Comments);
			setValue("delay1.DelayCodeId", delay.DelayCodeId);
		}
	}, [show, delay]);

	useEffect(() => {
		if (isSuccess) {
			if (delays && delays[0]) {
				setDelay(delays[0]);
			}
		}
	}, [isSuccess, delays]);

	return (
		<>
			{show && <HelmetDexteel title={`Split Delay - ${suffixTitle}`} />}
			<MesfModal
				title="SPLIT DELAY"
				open={show}
				handleClose={() => onHide(false)}
				id="split-delays-modal"
				maxWidth="lg"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						{isLoading ? (
							<CenteredLazyLoading />
						) : (
							<Grid2 container justifyContent="center">
								<Grid2
									size={{ md: 10 }}
									sx={{
										position: "relative",
										marginBottom: (theme) => theme.spacing(4),
										paddingTop: (theme) => theme.spacing(3),
									}}
								>
									<Typography
										fontSize={"0.75rem"}
										fontWeight={"bold"}
										textAlign={"center"}
										style={{
											position: "absolute",
											top: 0,
											transform: "translateX(-50%)",
											backgroundColor: "rgba(255, 255, 255, 0.7)",
											padding: "2px 4px",
											borderRadius: "4px",
											left: "25%",
											color: "#3f51b5",
										}}
									>
										{formatDuration(
											delay1End?.getTime()! - delay1Start?.getTime()!,
										)}
									</Typography>
									<Typography
										fontSize={"0.75rem"}
										fontWeight={"bold"}
										textAlign={"center"}
										style={{
											position: "absolute",
											top: 0,
											transform: "translateX(-50%)",
											backgroundColor: "rgba(255, 255, 255, 0.7)",
											padding: "2px 4px",
											borderRadius: "4px",
											left: "75%",
											color: "green",
										}}
									>
										{formatDuration(
											delay2End?.getTime()! - delay2Start?.getTime()!,
										)}
									</Typography>
									<CustomTimeSlider
										key={`${delay?.Start}-${delay?.End}`}
										startTime={
											delay?.Start ? new Date(delay?.Start) : new Date()
										}
										endTime={delay?.End ? new Date(delay?.End) : new Date()}
										onTimeChange={(newTime: Date) => {
											setValue("delay1.End", newTime);
											setValue("delay2.Start", newTime);
										}}
									/>
								</Grid2>
								<Grid2
									container
									size={{
										md: 5,
									}}
								>
									<Typography>Delay 1</Typography>
									<Grid2
										container
										size={{
											md: 12,
										}}
										spacing={1}
									>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay1.Start"
												control={control}
												rules={{ required: "Delay code is required" }}
												render={({ field, fieldState: { error } }) => (
													<TextField
														required
														label="Start"
														variant="outlined"
														value={dxtToLocalServerTime(
															field?.value,
															"HH:mm:ss",
														)}
														error={!!error}
														helperText={error?.message}
														disabled
														autoComplete="off"
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay1.DelayCodeId"
												control={control}
												render={({ field }) => (
													<DelayCodeTreePicker
														assetId={assetId!}
														value={field.value}
														onChange={(value, defaultCategoryId) => {
															field.onChange(value);
															setValue(
																"delay1.DelayCategoryId",
																defaultCategoryId,
															);
														}}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay1.End"
												control={control}
												render={({ field }) => (
													<TextField
														label="End"
														variant="outlined"
														value={dxtToLocalServerTime(
															field?.value,
															"HH:mm:ss",
														)}
														disabled
														autoComplete="off"
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay1.DelayCategoryId"
												control={control}
												render={({ field }) => (
													<DelayCategorySelector
														label="Category"
														value={field.value}
														onChange={field.onChange}
														assetId={delay?.AssetId}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 12 }}>
											<Controller
												name="delay1.Comments"
												control={control}
												render={({ field, fieldState: { error } }) => (
													<CommentsField
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
														getValues={getValues}
														rows={{ minRows: 3, maxRows: 3 }}
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
								<Grid2
									size={{
										md: 1,
									}}
								/>
								<Grid2
									container
									size={{
										md: 5,
									}}
								>
									<Typography>Delay 2</Typography>
									<Grid2
										container
										size={{
											md: 12,
										}}
										spacing={1}
									>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay2.Start"
												control={control}
												render={({ field }) => (
													<TextField
														label="Start"
														variant="outlined"
														value={dxtToLocalServerTime(
															field?.value,
															"HH:mm:ss",
														)}
														disabled
														autoComplete="off"
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay2.DelayCodeId"
												control={control}
												render={({ field }) => (
													<DelayCodeTreePicker
														assetId={assetId!}
														value={field.value}
														onChange={(value, defaultCategoryId) => {
															field.onChange(value);
															setValue(
																"delay2.DelayCategoryId",
																defaultCategoryId,
															);
														}}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay2.End"
												control={control}
												render={({ field }) => (
													<TextField
														label="End"
														variant="outlined"
														value={dxtToLocalServerTime(
															field?.value,
															"HH:mm:ss",
														)}
														disabled
														autoComplete="off"
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6 }}>
											<Controller
												name="delay2.DelayCategoryId"
												control={control}
												render={({ field }) => (
													<DelayCategorySelector
														label="Category"
														value={field.value}
														onChange={field.onChange}
														assetId={delay?.AssetId}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 12 }}>
											<Controller
												name="delay2.Comments"
												control={control}
												render={({ field, fieldState: { error } }) => (
													<CommentsField
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
														getValues={getValues}
														rows={{ minRows: 3, maxRows: 3 }}
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 90px 30px" }}>
						<Grid2 container spacing={1} justifyContent="flex-end">
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
									size="medium"
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Button
									fullWidth
									startIcon={isLoading && <CircularProgress size="1rem" />}
									disabled={isLoading}
									variant="contained"
									color="primary"
									type="submit"
									size="medium"
								>
									Split
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notificationErrorMessage}
				onHide={() => setNotificationErrorMessage("")}
				severity="error"
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Splitting Delays"
			/>
		</>
	);
};
